import { isValidElement, MouseEvent, ReactElement, ReactNode, useState } from 'react'

import { fontWeightRegular } from '@/constants'
import DialogTitle from '@components/mui/DialogTitle'
import { SizeProp } from '@fortawesome/fontawesome-svg-core'
import { faQuestionCircle } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
	Dialog,
	DialogContent,
	Fade,
	Paper,
	Popover,
	PopoverOrigin,
	SxProps,
	Typography,
	useTheme,
} from '@mui/material'
import { getTestId } from '@utils/helpers'

const TEXT_LENGTH_DIALOG_THRESHOLD = 500

type Props = {
	icon?: ReactElement // FontAwesome icon component
	children: ReactNode // Popover/Dialog content
	anchorOrigin?: PopoverOrigin
	transformOrigin?: PopoverOrigin
	size?: SizeProp
	paperSx?: SxProps
}

function getTextLength(node: ReactNode): number {
	if (typeof node === 'string') return node.length
	if (Array.isArray(node)) return node.reduce((sum: number, child) => sum + getTextLength(child), 0)
	if (isValidElement<{ children?: ReactNode }>(node)) return getTextLength(node.props.children)
	return 0
}

function isTextLikeNode(node: ReactNode): boolean {
	if (node === null || node === undefined || typeof node === 'boolean') return true
	if (typeof node === 'string' || typeof node === 'number') return true
	if (Array.isArray(node)) return node.every(isTextLikeNode)
	if (isValidElement(node)) return node.type === 'pre' || node.type === 'br'
	return false
}

/**
 * Component that displays a popover when hovering over a FontAwesome icon.
 */
export default function IconWithPopover({
	icon = null,
	children,
	anchorOrigin = { vertical: 'bottom', horizontal: 'left' },
	transformOrigin = { vertical: 'top', horizontal: 'left' },
	size = 'lg',
	paperSx = {},
	...rest
}: Props) {
	const testId = getTestId(rest, 'tooltip')
	const theme = useTheme()
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
	const [dialogOpen, setDialogOpen] = useState(false)

	const renderIcon =
		icon !== null ? (
			icon
		) : (
			<FontAwesomeIcon color={theme.palette.text.secondary} size={size} icon={faQuestionCircle} />
		)

	const isTextLike = isTextLikeNode(children)

	const isLongText = isTextLike && getTextLength(children) > TEXT_LENGTH_DIALOG_THRESHOLD

	const handleMouseEnter = (event: MouseEvent<HTMLElement>) => {
		if (isLongText) {
			setDialogOpen(true)
		} else {
			setAnchorEl(event.currentTarget)
		}
	}

	const handleMouseLeave = () => {
		if (!isLongText) {
			setAnchorEl(null)
		}
	}

	const handleDialogClose = () => {
		setDialogOpen(false)
	}

	const open = Boolean(anchorEl)

	const renderContent = (color: string) => (
		<Typography
			component="div"
			variant="body1"
			fontWeight={fontWeightRegular}
			color={color}
			sx={{ whiteSpace: 'pre-line' }}
			data-testid={testId}
		>
			{children}
		</Typography>
	)

	return (
		<>
			<div
				onMouseEnter={handleMouseEnter}
				onMouseLeave={handleMouseLeave}
				aria-owns={open ? 'mouse-over-popover' : undefined}
				aria-haspopup={isLongText ? 'dialog' : 'true'}
				style={{ cursor: 'pointer' }}
			>
				{renderIcon}
			</div>
			{isLongText ? (
				<Dialog
					open={dialogOpen}
					onClose={handleDialogClose}
					scroll="paper"
					fullWidth
					maxWidth="sm"
				>
					<DialogTitle onClose={handleDialogClose}>{null}</DialogTitle>
					<DialogContent data-testid="tooltip">
						{renderContent(theme.palette.text.primary)}
					</DialogContent>
				</Dialog>
			) : (
				<Popover
					sx={{ pointerEvents: 'none' }}
					open={open}
					anchorEl={anchorEl}
					anchorOrigin={anchorOrigin}
					transformOrigin={transformOrigin}
					onClose={handleMouseLeave}
					disableRestoreFocus
					TransitionComponent={Fade}
					transitionDuration={350}
					style={{ zIndex: 1500 }}
				>
					<Paper
						elevation={10}
						sx={{
							px: 2,
							py: 1,
							backgroundColor: theme.palette.secondary.main,
							...paperSx,
						}}
					>
						{isTextLike ? renderContent(theme.palette.secondary.contrastText) : children}
					</Paper>
				</Popover>
			)}
		</>
	)
}
